import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Image from 'next/image'
import { ArrowLeft, ArrowRight, Star } from 'lucide-react'

import { gsap } from '@/lib/gsap'
import { testimonials } from '@/constants/content'
import { Reveal } from '@/components/motion/reveal'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

export const Testimonials: FC = (): ReactNode => {
    const [index, setIndex] = useState(0)
    const quote = useRef<HTMLDivElement>(null)

    const current = testimonials[index]

    // Each change re-runs the same short rise, so stepping through the set
    // feels like one continuous gesture rather than a hard swap.
    useIsomorphicLayoutEffect(() => {
        const element = quote.current

        if (!element) {
            return
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }

        const context = gsap.context(() => {
            gsap.fromTo(
                element.querySelectorAll('[data-quote-part]'),
                { y: 26, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.85, ease: 'power3.out', stagger: 0.07 }
            )
        }, element)

        return () => context.revert()
    }, [index])

    const step = (direction: number) => {
        setIndex((value) => (value + direction + testimonials.length) % testimonials.length)
    }

    return (
        <section
            id='testimonials'
            className='edge bg-background py-20 md:py-28'
            aria-labelledby='testimonials-heading'
        >
            <div className='mx-auto max-w-[110rem]'>
                <p className='label mb-10 text-muted-foreground'>Testimonials</p>

                <h2 id='testimonials-heading' className='sr-only'>
                    What clients say
                </h2>

                <div className='grid gap-10 lg:grid-cols-[minmax(0,0.6fr)_minmax(0,1.4fr)] lg:gap-20'>
                    <Reveal className='space-y-8'>
                        <p className='max-w-xs text-[0.9375rem] leading-relaxed text-foreground/80'>
                            Here is what people have to say about working together. Real
                            sessions, real experiences, real feedback.
                        </p>

                        <div className='flex gap-2'>
                            <button
                                type='button'
                                onClick={() => step(-1)}
                                aria-label='Previous testimonial'
                                className='flex size-11 items-center justify-center rounded-full border border-border transition-colors duration-400 hover:bg-foreground hover:text-background'
                            >
                                <ArrowLeft className='size-4' aria-hidden='true' />
                            </button>

                            <button
                                type='button'
                                onClick={() => step(1)}
                                aria-label='Next testimonial'
                                className='flex size-11 items-center justify-center rounded-full border border-border transition-colors duration-400 hover:bg-foreground hover:text-background'
                            >
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    </Reveal>

                    <div ref={quote} aria-live='polite'>
                        <blockquote>
                            <p data-quote-part className='display-lg max-w-[20ch]'>
                                {current.quote}
                            </p>

                            <p
                                data-quote-part
                                className='mt-8 max-w-md text-[0.9375rem] leading-relaxed text-muted-foreground'
                            >
                                {current.detail}
                            </p>
                        </blockquote>

                        <div data-quote-part className='mt-8 flex items-center gap-3'>
                            <span className='relative size-10 overflow-hidden rounded-full'>
                                <Image
                                    src={current.avatar}
                                    alt=''
                                    fill
                                    sizes='40px'
                                    className='object-cover'
                                />
                            </span>

                            <span className='text-[0.8125rem] leading-tight'>
                                <span className='block font-medium'>{current.name}</span>
                                <span className='block text-muted-foreground'>{current.role}</span>
                            </span>

                            <span className='ml-4 flex items-center gap-1.5 border-l border-border pl-4'>
                                <span className='flex gap-0.5' aria-hidden='true'>
                                    {[0, 1, 2, 3, 4].map((star) => (
                                        <Star
                                            key={star}
                                            className='size-3 fill-[color:var(--gold)] text-[color:var(--gold)]'
                                        />
                                    ))}
                                </span>

                                <span className='text-[0.6875rem] font-medium'>
                                    {current.rating} / 5
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
